import React from 'react';
import { Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { useQuery } from '@tanstack/react-query';
import FinancialService from '../../../AppSettings/services/FinancialService';
import VenExpService from '../../../AppSettings/services/VenExpService';
import OppExpService from '../../../AppSettings/services/OppExpService';
import GenExpensesService from '../../../AppSettings/services/GenExpensesService';
import FixBillService from '../../../AppSettings/services/FixBillService';
import SectionCard from '../../../Components/SectionCard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCoins } from '@fortawesome/free-solid-svg-icons';
import './BillingExpensesPie.css';
import { normalizeToArray } from '../../../helpers/responseHelpers';
import { computeBilledTimeAmount } from '../../../helpers/billingHelpers';

ChartJS.register(ArcElement, Tooltip, Legend);

interface FinancialTotals {
  billableTime: number;
  fixedFees: number;
  other: number;
  vendor: number;
  operating: number;
  general: number;
  fixedBills: number;
}

const BillingExpensesPie: React.FC = () => {
  const { data: totals, isLoading, error } = useQuery<FinancialTotals>({
    queryKey: ['dashboard-financial-totals'],
    queryFn: async () => {
      const [
        billedTimeData,
        fixedFeesData,
        otherBillingData,
        vendorData,
        operatingData,
        generalData,
        fixedBillsData,
      ] = await Promise.all([
        FinancialService.getAllBilledTime(),
        FinancialService.getAllAssignedFinancialData(),
        FinancialService.getAllOtherBilling(),
        VenExpService.getAllVenExpenses(),
        OppExpService.getAllOppExpenses(),
        GenExpensesService.getAllGenExpenses(),
        FixBillService.getAllFixBillExpenses(),
      ]);

      const sum = (arr: any[]) =>
        arr.reduce((s: number, i: any) => s + (Number.isFinite(parseFloat(i.amount)) ? parseFloat(i.amount) : 0), 0);

      const billedTimeSum = (arr: any[]) =>
        arr.reduce((s: number, i: any) => s + computeBilledTimeAmount(i), 0);

      const fixedFeesList = normalizeToArray(fixedFeesData as any);
      const fixedFeesSum = fixedFeesList.reduce(
        (s: number, m: any) =>
          s + ((m.fixed_fees || []).reduce((ms: number, f: any) => ms + (Number.isFinite(parseFloat(f.fees)) ? parseFloat(f.fees) : 0), 0)),
        0,
      );

      return {
        billableTime: billedTimeSum(normalizeToArray(billedTimeData)),
        fixedFees: fixedFeesSum,
        other: sum(normalizeToArray(otherBillingData)),
        vendor: sum(normalizeToArray(vendorData)),
        operating: sum(normalizeToArray(operatingData)),
        general: sum(normalizeToArray(generalData)),
        fixedBills: sum(normalizeToArray(fixedBillsData)),
      };
    },
    staleTime: 2 * 60 * 1000,
  });

  const billableTimeTotal = totals?.billableTime ?? 0;
  const fixedFeesTotal = totals?.fixedFees ?? 0;
  const otherTotal = totals?.other ?? 0;
  const vendorTotal = totals?.vendor ?? 0;
  const operatingTotal = totals?.operating ?? 0;
  const generalTotal = totals?.general ?? 0;
  const fixedBillsTotal = totals?.fixedBills ?? 0;

  const totalBilling = billableTimeTotal + fixedFeesTotal + otherTotal;
  const totalExpenses = vendorTotal + operatingTotal + generalTotal + fixedBillsTotal;
  const netIncome = totalBilling - totalExpenses;
  const grandTotal = totalBilling + totalExpenses;

  const billingPercentage = grandTotal > 0 ? (totalBilling / grandTotal) * 100 : 0;
  const expensesPercentage = grandTotal > 0 ? (totalExpenses / grandTotal) * 100 : 0;

  const data = {
    labels: [
      'Billable Time', 'Fixed Fees', 'Other',
      'Vendor Services', 'Operating', 'General', 'Fixed Bills',
    ],
    datasets: [{
      label: 'Amount',
      data: [
        billableTimeTotal, fixedFeesTotal, otherTotal,
        vendorTotal, operatingTotal, generalTotal, fixedBillsTotal,
      ],
      backgroundColor: [
        'rgba(30, 79, 251, 0.8)', 'rgba(59, 130, 246, 0.8)', 'rgba(96, 165, 250, 0.8)',
        'rgba(239, 68, 68, 0.8)', 'rgba(248, 113, 113, 0.8)', 'rgba(252, 165, 165, 0.8)', 'rgba(254, 202, 202, 0.8)',
      ],
      borderColor: [
        'rgba(30, 79, 251, 1)', 'rgba(59, 130, 246, 1)', 'rgba(96, 165, 250, 1)',
        'rgba(239, 68, 68, 1)', 'rgba(248, 113, 113, 1)', 'rgba(252, 165, 165, 1)', 'rgba(254, 202, 202, 1)',
      ],
      borderWidth: 2,
    }],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'right' as const,
        labels: {
          padding: 8, usePointStyle: true, pointStyle: 'circle',
          font: { size: 10 }, boxWidth: 8, boxHeight: 8
        }
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            const label = context.label || '';
            const value = context.parsed || 0;
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
            return `${label}: $${value.toLocaleString()} (${percentage}%)`;
          }
        }
      }
    }
  };

  const iconElement = (
    <div className="billingExpensesPie-icon-container">
      <FontAwesomeIcon icon={faCoins} className="billingExpensesPie-icon" />
    </div>
  );

  if (isLoading) {
    return (
      <SectionCard title="Billing vs. Expenses" right={iconElement}>
        <div>Loading...</div>
      </SectionCard>
    );
  }

  if (error) {
    return (
      <SectionCard title="Billing vs. Expenses" right={iconElement}>
        <div>Error: {(error as Error).message}</div>
      </SectionCard>
    );
  }

  return (
    <SectionCard 
      title="Billing vs. Expenses" 
      right={iconElement}
      className="billingExpensesPie-container"
    >
      <div className="billingExpensesPie-content">
        <div className="billingExpensesPie-chart-wrapper">
          <Pie data={data} options={options} />
        </div>
        <div className="billingExpensesPie-summary">
          <div className="billingExpensesPie-summary-item">
            <div className="billingExpensesPie-summary-label">
              <span className="billingExpensesPie-dot billingExpensesPie-dot--billing"></span>
              <span>Total Billing</span>
            </div>
            <span className="billingExpensesPie-summary-value">{billingPercentage.toFixed(1)}%</span>
          </div>
          <div className="billingExpensesPie-summary-item">
            <div className="billingExpensesPie-summary-label">
              <span className="billingExpensesPie-dot billingExpensesPie-dot--expenses"></span>
              <span>Total Expenses</span>
            </div>
            <span className="billingExpensesPie-summary-value">{expensesPercentage.toFixed(1)}%</span>
          </div>
          <div className="billingExpensesPie-summary-item">
            <div className="billingExpensesPie-summary-label">
              <span className="billingExpensesPie-dot billingExpensesPie-dot--income"></span>
              <span>Net Income</span>
            </div>
            <span className="billingExpensesPie-summary-value">${netIncome.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
        </div>
      </div>
    </SectionCard>
  );
};

export default BillingExpensesPie;
